<template>
  <div class="tenant-description">
    <!-- header -->
    <div class="page-admin-tenant-card__subtitle">
      <div
        class="page-admin-tenant-card__subtitle--p"
        v-tooltip="{
          content: this.tenantHasTenderScoringAccess() ? '' : this.$t('page.admin-tenant.access.scoring'),
          placement: 'top',
        }"
      >
        {{ $t('page.admin-tenant.services-description.title') }}
        <BaseTag tone="ai" preset="soft" icon="ai">
          {{ $t('tags.ai') }}
        </BaseTag>
        <BaseIcon v-if="!tenantHasTenderScoringAccess()" name="lock" class="base-navigation-buttons__lock" />
      </div>

      <div class="page-admin-tenant-card__actions" v-if="isAdminTenant && this.tenantHasTenderScoringAccess()">
        <BaseButton
          isSmall
          isSquare
          isGrey
          class="page-admin-tenant-card__action-button"
          :disabled="isGenerating || !tenant.websiteUrl"
          @click.native="generatePreview"
          v-tooltip="{
            content: !!tenant.websiteUrl ? '' : this.$t('page.admin-tenant.services-description.tooltip-url-required'),
          }"
        >
          <template>
            <span class="nowrap">{{
              isGenerating
                ? $t('page.admin-tenant.services-description.in-progress')
                : $t('page.admin-tenant.services-description.ai-generate')
            }}</span>
            <BaseCircularLoadingBar v-if="isGenerating" :size="14" :width="2" />
            <BaseIcon v-else name="creation" />
          </template>
        </BaseButton>
        <BaseButton
          isSmall
          isSquare
          isGrey
          class="page-admin-tenant-card__action-button"
          @click.native="openEditDescriptionDialog"
        >
          {{ $t('actions.edit') }}
          <BaseIcon name="edit-2" />
        </BaseButton>
      </div>
    </div>

    <!-- description -->
    <p class="page-admin-tenant-card__subtitle--description">
      {{ $t('page.admin-tenant.services-description.description') }}
    </p>

    <div v-if="isQueueVisible" class="tenant-queue__container">
      <div class="tenant-queue__steps-container">
        <div v-for="(step, index) in queueSteps" :key="index" class="tenant-queue__step">
          <span class="icon">
            <template v-if="index < queueStepIndex">
              <BaseIcon name="check-circle" class="tenant-queue__step-completed-icon" />
            </template>
            <template v-else-if="index === queueStepIndex">
              <BaseCircularLoadingBar :size="16" :width="2" />
            </template>
            <template v-else>
              <span class="tenant-queue__step-not-started-icon"></span>
            </template>
          </span>
          <span>{{ step }}</span>
        </div>
      </div>
    </div>

    <!-- tabs -->
    <div class="tabs" v-if="tenantHasTenderScoringAccess()">
      <button :class="['tab', { active: activeTab === 'fr' }]" @click="activeTab = 'fr'">
        {{ $t('page.admin-tenant.labels-fr') }}
      </button>
      <button :class="['tab', { active: activeTab === 'en' }]" @click="activeTab = 'en'">
        {{ $t('page.admin-tenant.labels-en') }}
      </button>
    </div>

    <!-- tab content -->
    <div class="page-admin-tenant-card__description-content">
      <!-- ai preview -->
      <div v-if="isPreviewVisible" class="page-admin-tenant-card__description preview">
        <div class="page-admin-tenant-card__description--ai-generated-title">
          <BaseIcon name="creation" />
          {{ $t('page.admin-tenant.services-description.ai-preview') }}
        </div>
        <div class="page-admin-tenant-card__description--ai-generated-content">
          <div v-if="activeTab === 'fr'">{{ previewDescription.fr }}</div>
          <div v-else>{{ previewDescription.en }}</div>
        </div>
        <div class="preview-actions">
          <BaseButton isSmall isGrey class="mr-8" @click.native="rejectPreview">
            {{ $t('actions.reject') }}
          </BaseButton>
          <BaseButton isSmall @click.native="approvePreview">
            {{ $t('actions.save') }}
          </BaseButton>
        </div>
      </div>

      <!-- edit description -->
      <div v-if="!getActiveDescription && !isPreviewVisible">
        <template v-if="isManager && tenantHasTenderScoringAccess()">
          <div v-if="activeTab === 'fr'">
            <FormInput
              is-textarea
              is-compact
              name="tenant-description-edit-fr"
              :rows="5"
              :value="editableDescription.fr"
              :placeholder="$t('page.admin-tenant.services-description.empty-description')"
              @input="editableDescription.fr = $event"
              @keydown.enter="saveDescription('fr')"
            />
            <div class="manual-actions">
              <BaseButton isSmall :disabled="!canSaveFr" @click.native="saveDescription('fr')">
                {{ $t('actions.save') }}
              </BaseButton>
            </div>
          </div>

          <div v-if="activeTab === 'en'">
            <FormInput
              is-textarea
              is-compact
              name="tenant-description-edit-en"
              :rows="5"
              :value="editableDescription.en"
              :placeholder="$t('page.admin-tenant.services-description.empty-description')"
              @input="editableDescription.en = $event"
              @keydown.enter="saveDescription('en')"
            />
            <div class="manual-actions">
              <BaseButton isSmall :disabled="!canSaveEn" @click.native="saveDescription('en')">
                {{ $t('actions.save') }}
              </BaseButton>
            </div>
          </div>
        </template>
        <div v-else class="page-admin-tenant-card__empty">
          {{ $t('basic-words.none') }}
        </div>
      </div>

      <!-- description display -->
      <div v-else-if="isAiGenerated && !isPreviewVisible" class="page-admin-tenant-card__description ai-generated">
        <div class="page-admin-tenant-card__description--ai-generated-title">
          <BaseIcon name="creation" />
          {{ $t('page.admin-tenant.services-description.ai-description') }}
        </div>
        <div class="page-admin-tenant-card__description--ai-generated-content">{{ getActiveDescription }}</div>
      </div>

      <div class="page-admin-tenant-card__description" v-if="getActiveDescription && getActiveDescription.length > 0">
        {{ getActiveDescription }}
      </div>
    </div>
    <EditTenantDescription
      ref="editTenantDescription"
      @update-description="(language, desc) => updateDescription(language, desc)"
    ></EditTenantDescription>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import BaseTag from '@/components/base/BaseTag.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import FormInput from '@/components/form/FormInput';
import EditTenantDescription from '@/components/page-admin-tenants/EditTenantDescription.vue';

import { tenantHasTenderScoringAccess } from '@/util/module-access-util';
import { toMoment } from '@/util/date-util';

export default {
  name: 'TenantDescription',
  components: { BaseCircularLoadingBar, BaseIcon, BaseTag, EditTenantDescription, BaseButton, FormInput },
  data() {
    return {
      activeTab: 'fr',
      editableDescription: { fr: '', en: '' },
      isAiGenerated: false,
      isGenerating: false,
      previewDescription: { fr: '', en: '' },
      isQueueVisible: false,
      queueStepIndex: -1,
    };
  },
  computed: {
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('auth', ['isManager', 'isAdminTenant']),
    getActiveDescription() {
      if (!this.tenant || !this.tenant.description) return '';
      return this.activeTab === 'fr' ? this.tenant.description.fr : this.tenant.description.en;
    },
    canSaveFr() {
      return !!(this.editableDescription.fr && this.editableDescription.fr.trim().length > 0);
    },
    canSaveEn() {
      return !!(this.editableDescription.en && this.editableDescription.en.trim().length > 0);
    },
    queueSteps() {
      return [this.$t('page.admin-tenant.website.fetching'), this.$t('page.admin-tenant.website.generating')];
    },
    isPreviewVisible() {
      return !!this.previewDescription.en && !!this.previewDescription.fr;
    },
  },
  methods: {
    ...mapActions('tenant', [
      'getTenant',
      'updateTenant',
      'updateTenantDescription',
      'approveTenantDescriptionPreview',
      'getTenantWebsite',
      'generateTenantDescriptionPreview',
    ]),
    toMoment,
    tenantHasTenderScoringAccess,
    async generatePreview() {
      this.isGenerating = true;
      this.isQueueVisible = true;
      this.queueStepIndex = 0;

      try {
        const url = await this.getTenantWebsite();

        this.queueStepIndex = 1;
        const previewResponse = await this.generateTenantDescriptionPreview(url);

        this.queueStepIndex = 2;
        this.previewDescription = previewResponse;
      } catch (error) {
        this.$toast.error(error);
      } finally {
        this.isQueueVisible = false;
        this.queueStepIndex = -1;
        this.isGenerating = false;
      }
    },
    async approvePreview() {
      try {
        // update tenant description in auth service
        const updatedDescription = {
          description: {
            ...this.tenant.description,
            fr: this.previewDescription.fr.trim() || '',
            en: this.previewDescription.en.trim() || '',
          },
        };

        await this.updateTenant({ tenantId: this.tenant.id, tenant: updatedDescription });

        // update tenant description in tenant settings service
        const description = {
          fr: this.previewDescription.fr.trim() || '',
          en: this.previewDescription.en.trim() || '',
          fr_model: this.previewDescription.fr_model || 'ai',
          en_model: this.previewDescription.en_model || 'ai',
          snapshot: this.previewDescription.snapshot || '{}',
        };

        await this.approveTenantDescriptionPreview(description);

        this.resetPreviewState();
        this.$toast.success(this.$t('toast.success'));
      } catch (e) {
        this.$toast.error(e && e.message ? e.message : this.$t('basic-words.error'));
      }
    },
    rejectPreview() {
      this.resetPreviewState();
      this.$toast.success(this.$t('toast.rejected-description'));
    },
    resetPreviewState() {
      this.isPreviewVisible = false;
      this.isQueueVisible = false;
      this.queueStepIndex = -1;
      this.previewDescription = { fr: '', en: '' };
      this.previewMeta = { fr_model: '', en_model: '' };
    },
    async saveDescription(lang) {
      const text = lang === 'fr' ? this.editableDescription.fr || '' : this.editableDescription.en || '';
      if (!text || text.trim().length === 0) return;

      this.updateDescription(lang, text.trim());
    },
    async updateDescription(language, newDescription) {
      try {
        const updatedDescription = {
          description: {
            ...this.tenant.description,
            [language]: newDescription.trim(),
          },
        };

        // update tenant description in auth service
        await this.updateTenant({ tenantId: this.tenant.id, tenant: updatedDescription });

        // update tenant description in tenant settings service
        await this.updateTenantDescription({
          fr: updatedDescription.description.fr || '',
          en: updatedDescription.description.en || '',
        });

        // refresh tenant data
        await this.getTenant(this.tenant.id);
      } catch (error) {
        this.$toast.error(error);
      }
    },
    openEditDescriptionDialog() {
      this.$refs.editTenantDescription.openDialog(this.tenant, this.activeTab);
    },
  },
  watch: {
    getActiveDescription: {
      immediate: true,
      handler(newVal) {
        if (this.activeTab === 'fr') {
          this.editableDescription.fr = newVal || '';
        } else if (this.activeTab === 'en') {
          this.editableDescription.en = newVal || '';
        }
      },
    },
  },
};
</script>

<style lang="scss">
.page-admin-tenant-card {
  &__description-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__description {
    padding: 16px;
    background: $color-neutral-pale;
    border-radius: $border-radius-root;
    font-weight: normal;

    white-space: pre-wrap;
    word-wrap: break-word;

    &.ai-generated {
      background: $color-success-pale;
      border: 1px solid $color-green;
      font-weight: normal;
    }

    &--ai-generated-title {
      font-weight: bold;
      margin-bottom: 8px;
      display: flex;
      gap: 16px;
      align-items: center;

      .base-icon {
        width: 18px;
      }
    }

    &--ai-generated-content {
      margin-left: 32px;
    }

    &.preview {
      background: $color-purple-100;
      border: 1px solid $color-purple-500;
    }
  }

  .tenant-description {
    .tabs {
      margin-bottom: 16px;
    }

    // Phones: 44px to tap, whatever it looks.
    .tab {
      @include phone-touch-area;
      padding: 8px 16px;
      margin-right: 0;
      cursor: pointer;
      font-size: 16px;
      border-radius: 0;
      border: none;

      &.active {
        color: $color-normal;
        border-bottom: 3px solid $color-normal;
      }
    }
    .preview-actions {
      margin-top: 8px;
      display: flex;
      justify-content: flex-end;
    }

    .manual-actions {
      margin-top: 8px;
      display: flex;
      justify-content: flex-end;
    }

    .tenant-queue {
      &__container {
        color: $color-neutral;
        background: $color-white;
        border: 1px solid $color-border;
        border-radius: $border-radius-root;
        padding: 12px 16px;
        margin: 8px 0 12px 0;
      }

      &__steps-container {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        flex-direction: column;
        gap: 12px;
      }

      &__step {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
      }

      &__step-not-started-icon {
        display: inline-block;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 1px solid $color-neutral;
      }

      &__step-completed-icon {
        width: 16px;
        color: $color-green;
      }
    }
  }
}
</style>
